import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, IconButton, Snackbar, Alert, Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { adminFetch } from '../../lib/adminAuth';

const DEFAULTS = {
  pageTitle: 'Help & Resources',
  pageSubtitle: 'Find answers to common questions and learn how to get the most out of your rewards system.',
  faqs: [
    { question: 'How do I set up a reward campaign?', answer: 'To set up a reward campaign, navigate to the Rewards page and click "Update Reward Setup". Then, define the reward details (for example, "Buy 10 coffees, get 1 free") and save your settings. This will launch your campaign immediately.' },
    { question: 'How do customers earn and redeem rewards?', answer: 'Customers collect a stamp for each purchase by scanning the QR code in your store. Once they accumulate the required number of stamps, they can claim their free reward via the mobile app.' },
    { question: 'How can I track reward performance?', answer: 'Your dashboard provides real-time metrics on total redemptions, customer activity, and overall campaign performance. Use these insights to optimize your offers.' },
  ],
  supportHeading: 'Need More Help?',
  supportText: "If you can't find what you're looking for, reach out to our support team and we'll get back to you as soon as possible.",
  supportEmail: 'support@rewarding.com',
};

export default function AdminHelp() {
  const [content, setContent] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const res = await adminFetch('/api/admin/help-content');
        const data = await res.json();
        if (data && Object.keys(data).length > 1) {
          setContent({
            pageTitle: data.pageTitle || DEFAULTS.pageTitle,
            pageSubtitle: data.pageSubtitle || DEFAULTS.pageSubtitle,
            faqs: data.faqs && data.faqs.length > 0 ? data.faqs : DEFAULTS.faqs,
            supportHeading: data.supportHeading || DEFAULTS.supportHeading,
            supportText: data.supportText || DEFAULTS.supportText,
            supportEmail: data.supportEmail || DEFAULTS.supportEmail,
          });
        }
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const updateField = (field, value) => setContent(prev => ({ ...prev, [field]: value }));

  const updateFaq = (index, field, value) => {
    setContent(prev => {
      const faqs = [...prev.faqs];
      faqs[index] = { ...faqs[index], [field]: value };
      return { ...prev, faqs };
    });
  };

  const addFaq = () => {
    setContent(prev => ({ ...prev, faqs: [...prev.faqs, { question: '', answer: '' }] }));
  };

  const removeFaq = (index) => {
    setContent(prev => ({ ...prev, faqs: prev.faqs.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin/help-content', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (res.ok) setSnack({ open: true, message: 'Help content saved!', severity: 'success' });
      else throw new Error();
    } catch {
      setSnack({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(DEFAULTS);
    setSnack({ open: true, message: 'Reset to defaults (save to apply)', severity: 'info' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>Help & FAQ</Typography>
          <Typography variant="body2" sx={{ color: '#888' }}>Manage the Help page content and FAQ entries</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button startIcon={<RestoreIcon />} onClick={handleReset} sx={{ textTransform: 'none', borderRadius: 2, color: '#888' }}>
            Reset
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#16213e' } }}>
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </Box>
      </Box>

      {/* Page Header */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Page Header</Typography>
        <TextField label="Page Title" fullWidth value={content.pageTitle} onChange={e => updateField('pageTitle', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="Page Subtitle" fullWidth multiline rows={2} value={content.pageSubtitle} onChange={e => updateField('pageSubtitle', e.target.value)} size="small" />
      </Paper>

      {/* FAQ Entries */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpOutlineIcon sx={{ color: '#1976d2' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>FAQ Entries</Typography>
            <Typography variant="caption" sx={{ color: '#999', ml: 1 }}>({content.faqs.length} items)</Typography>
          </Box>
          <Button startIcon={<AddIcon />} onClick={addFaq} size="small" sx={{ textTransform: 'none', borderRadius: 2 }}>
            Add FAQ
          </Button>
        </Box>

        {content.faqs.map((faq, index) => (
          <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e8e8e8', borderRadius: 2, bgcolor: '#fafafa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#999', fontWeight: 600 }}>FAQ #{index + 1}</Typography>
              <IconButton size="small" onClick={() => removeFaq(index)} sx={{ color: '#ccc', '&:hover': { color: '#e53935' } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField label="Question" fullWidth value={faq.question} onChange={e => updateFaq(index, 'question', e.target.value)} sx={{ mb: 1.5 }} size="small" />
            <TextField label="Answer" fullWidth multiline rows={3} value={faq.answer} onChange={e => updateFaq(index, 'answer', e.target.value)} size="small" />
          </Box>
        ))}
      </Paper>

      {/* Contact Support */}
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Contact Support Section</Typography>
        <TextField label="Heading" fullWidth value={content.supportHeading} onChange={e => updateField('supportHeading', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="Support Text" fullWidth multiline rows={2} value={content.supportText} onChange={e => updateField('supportText', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="Support Email" fullWidth value={content.supportEmail} onChange={e => updateField('supportEmail', e.target.value)} size="small" />
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
